import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { CarrierType, CompanyType, RailVisitResponseDto, VesselVisitDto } from '@planning/app/api'
import { CustomDatePicker } from '@planning/components/CustomDatePicker'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import { carrierVisitTypes } from '@planning/constants'
import { railVisitService, vesselVisitService } from '@planning/services'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { RequiredField } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  NumericReleaseControlViewStore,
  NumericReleaseFormData,
} from '../../../Stores/NumericReleaseControlViewStore'

interface Props {
  viewStore: NumericReleaseControlViewStore
  onSubmit: (data: NumericReleaseFormData) => Promise<void>
}

export const NumericReleaseForm: FC<Props> = observer(({ viewStore, onSubmit }) => {
  const { t } = useTranslate()

  const form = useForm<NumericReleaseFormData>({
    defaultValues: {
      referenceNumber: undefined,
      shippingLineId: undefined,
      amount: undefined,
      modeOfTransport: undefined,
      carrierVisitId: undefined,
      validityDate: undefined,
      transportCompanyId: undefined,
    },
  })

  const { control, getValues, reset, watch, handleSubmit, setError, clearErrors, formState } = form
  const { errors } = formState

  // todo: [north] will replace once there is vesselItemStore
  const [selectedVisit, setSelectedVisit] = useState<VesselVisitDto | RailVisitResponseDto | null>(
    null,
  )

  useAsyncFetch(async () => {
    if (viewStore.selectedNumericRelease) {
      const {
        referenceNumber,
        shippingLineId,
        modeOfTransport,
        transportCompanyId,
        carrierVisitId,
        validityDate,
      } = viewStore.selectedNumericRelease.data

      reset({
        referenceNumber,
        shippingLineId,
        modeOfTransport,
        transportCompanyId,
        carrierVisitId,
        validityDate: validityDate ? dayjs(validityDate) : undefined,
      })

      if (carrierVisitId) {
        let visit = null
        if (modeOfTransport === CarrierType.Train) {
          visit = await railVisitService.getById(carrierVisitId)
        } else if (modeOfTransport === CarrierType.Vessel) {
          visit = await vesselVisitService.getById(carrierVisitId)
        }

        setSelectedVisit(visit)
      }
    }
  }, [reset, viewStore.selectedNumericRelease])

  // [Review] TODO: Why all the state here? Would be better to use FormStores.
  const watchModeOfTransport = watch('modeOfTransport')

  const isValidityDateValid = (date: string | number | Date | dayjs.Dayjs | null | undefined) => {
    // Temporary workaround: validate by exact timestamp (UTC flow), not calendar-day boundary.
    const isBeforeNow = isValidityExpiredByNow(date)
    if (isBeforeNow) {
      setError('validityDate', {
        type: 'datePast',
        message: t('pleaseSelectAFutureDate', 'Please select a future date'),
      })
    } else {
      clearErrors('validityDate')
    }

    return !isBeforeNow
  }

  const submit = async (data: NumericReleaseFormData) => {
    let isValid = true
    if (data.validityDate) {
      isValid = isValidityDateValid(data.validityDate)
    }

    if (isValid) {
      await onSubmit(data)
    }
  }

  const canEdit = useMemo(
    () => (viewStore.selectedNumericRelease ? viewStore.selectedNumericRelease.canEdit : true),
    [viewStore.selectedNumericRelease],
  )

  return (
    <form id='numeric-release-form' onSubmit={handleSubmit(submit)} autoComplete='off' noValidate>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            control={control}
            name='shippingLineId'
            rules={{ required: true }}
            render={({ field }) => (
              <CompanyAutocomplete
                disabled={!canEdit}
                label={t('operator', 'Operator')}
                selectedCompanyId={viewStore.selectedNumericRelease?.data.shippingLineId}
                handleOnChange={operator => {
                  field.onChange(operator?.id)
                }}
                filterByTypes={[CompanyType.ShippingLine]}
                dataCy='operator-autocomplete'
                required
                error={!!errors.shippingLineId}
                helperText={errors.shippingLineId ? 'Field is required.' : ''}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            control={control}
            name='referenceNumber'
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={!canEdit}
                required
                label={t('referenceNumber', 'Reference Number')}
                onChange={e => {
                  field.onChange(e.target.value.toUpperCase())
                }}
                variant='outlined'
                data-cy='create-numeric-release-form-reference'
                error={!!errors.referenceNumber}
                helperText={errors.referenceNumber ? 'Field is required.' : ''}
              />
            )}
          />
        </Grid>

        <Grid size={6}>
          <Controller
            control={control}
            name='validityDate'
            render={({ field, fieldState }) => (
              <CustomDatePicker
                {...field}
                value={field.value ?? null}
                label={t('validityDate', 'Validity Date')}
                onChange={e => {
                  const d = e ? dayjs(e).endOf('day') : null
                  isValidityDateValid(d)
                  field.onChange(d)
                }}
                disablePast
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant='h4' sx={{ marginTop: '1rem' }}>
            {t('modeOfTransport', 'Mode of transport')}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Controller
            control={control}
            name={`modeOfTransport`}
            rules={{ required: true }}
            render={({ field: { onChange } }) => (
              <FormControl fullWidth>
                <InputLabel sx={{ verticalAlign: 'center' }}>
                  {t('modeOfTransport', 'Mode of transport')} <RequiredField />
                </InputLabel>
                <Select
                  disabled={!canEdit}
                  label={t('modeOfTransport', 'Mode of transport')}
                  required
                  value={getValues(`modeOfTransport`) ?? ''}
                  onChange={onChange}
                  data-cy='create-numeric-release-form-transport'
                  error={!!errors.modeOfTransport}
                >
                  {carrierVisitTypes.map(d => {
                    return (
                      <MenuItem key={d.key} value={d.name}>
                        {d.name}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={6}>
          {watchModeOfTransport === CarrierType.Vessel && (
            <Box>
              <Controller
                control={control}
                name={`carrierVisitId`}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <VesselVisitAutoComplete
                    disabled={!canEdit}
                    required
                    dataCy='vessel-autocomplete'
                    selectedVesselVisit={
                      selectedVisit ? (selectedVisit as VesselVisitDto) : undefined
                    }
                    handleOnChange={visit => onChange(visit?.id)}
                  />
                )}
              />
            </Box>
          )}
          {watchModeOfTransport === CarrierType.Train && (
            <Box>
              <Controller
                control={control}
                name={`carrierVisitId`}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <RailVisitAutoComplete
                    disabled={!canEdit}
                    required
                    selectedRailVisit={selectedVisit as RailVisitResponseDto}
                    handleOnChange={visit => {
                      if (visit) {
                        onChange(visit.id)
                        setSelectedVisit(visit)
                      }
                    }}
                    error={!!errors.carrierVisitId}
                    helperText={
                      errors.carrierVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                    }
                    cargoType='Container'
                    dataCy='rail-visit-autocomplete'
                  />
                )}
              />
            </Box>
          )}
          {watchModeOfTransport === CarrierType.Truck && (
            <Box>
              <Controller
                control={control}
                name={`transportCompanyId`}
                render={({ field: { onChange } }) => (
                  <CompanyAutocomplete
                    label={t('transportCompany', 'Transport Company')}
                    filterByTypes={[CompanyType.TransportCompany]}
                    selectedCompanyId={viewStore.selectedNumericRelease?.data.transportCompanyId}
                    disabled={!canEdit}
                    handleOnChange={company => {
                      if (company) {
                        onChange(company.id)
                      }
                    }}
                    error={!!errors.carrierVisitId}
                    helperText={
                      errors.carrierVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                    }
                    dataCy='transport-company-autocomplete'
                  />
                )}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </form>
  )
})
